import Upgrade from '../../core/Upgrade';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import { Typography } from '@material-ui/core';
import DialogMultiActions from '../../core/DialogMultiActions';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import Errored from '../../core/Errored';
import CreateButton from '../../core/CreateButton';
import Help from '../../core/Help';
import MenuSelector from '../../core/MenuSelector';
import PrimaryButton from '../../core/PrimaryButton';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import { globalStyles } from '../../core/style/gstheme';
import TitledPanel from '../../core/TitledPanel';
import TitledTextField from '../../core/TitledTextField';
import { lightSanitiseText, makeCancelable } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import { SUGGESTION_TYPE_OPTIONS } from '../constants';
import ConceptChip from '../core/ConceptChip';
import FindConcept from './FindConcept';
import SuggestionField from './SuggestionField';
import SubscriptionUsers from '../../core/SubscriptionUsers';
import SuggestionAltLabelField from './SuggestionAltLabelField';
import SuggestionPrefLabelField from './SuggestionPrefLabelField';

const styles = Object.assign({
    root: {
        "& .MuiPaper-root": {
            overflowY: 'visible'
        }
    },
    bigPanel: { marginLeft: 12, width: 330, backgroundColor: '#f5f5f5', padding: 12 }
}, globalStyles);

const loadOptions = {
    predicates: [
        "http://www.w3.org/2004/02/skos/core#inScheme",
        "http://www.w3.org/2004/02/skos/core#prefLabel",
        "http://www.w3.org/2002/07/owl#deprecated",
        "http://www.w3.org/2008/05/skos-xl#prefLabel",
        "http://www.w3.org/2004/02/skos/core#narrower",
        "http://www.w3.org/1999/02/22-rdf-syntax-ns#type"
    ]
};

class AddSuggestionDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            text: "",
            suggestionType: props.defaultType || undefined,
            suggestedLabel: "",
            labelIsValid: false,
            isErrored: false,
            target: undefined,
            user: "All",
            inSchemeResources: [],
            parentTarget: undefined,
            lang: this.props.defaultLang,
            labelLang: this.props.defaultLang,
            allowNotifications: GS.getActiveSubscriptionType() === "Managed" || GS.getActiveSubscriptionType() === "Trial" || GS.getActiveSubscriptionType() === "Premium"
        };
        this.cancellable = null;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    async loadConcepts() {
        let resources = this.props.data.broader || [];
        if (resources.length === 0) return;
        try {
            await this.loadData(resources).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            const inSchemeResources = resources.filter(resource => {
                const data = GS.data(resource, this.props.graph, false);
                if (data) {
                    const isInScheme = data.inScheme && data.inScheme.includes(this.props.schemeIRI) ? true : false;
                    return isInScheme;
                }
                return false;
            });
            let parentTarget = null;
            if (inSchemeResources.length === 1) parentTarget = inSchemeResources[0];
            this.setState({ isLoading: false, inSchemeResources, parentTarget });
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
    }

    loadData(data) {
        this.cancellable = makeCancelable(GS.loadData(data, loadOptions, this.props.graph));
        return this.cancellable;
    }

    handleClickOpen = () => {
        this.setState({
            open: true,
            lang: this.props.defaultLang
        });
    };

    addSuggestion = (notify = false) => {
        const user = this.state.user !== "All" ? this.state.user : null;
        const suggestionData = {
            suggestionType: this.state.suggestionType,
            lang: this.state.lang,
            labelLang: this.state.labelLang,
            text: this.state.text,
            target: this.state.target,
            parentTarget: this.state.parentTarget,
            suggestedLabel: this.state.suggestedLabel,
            user,
            notify
        };
        this.props.onAddSuggestion(suggestionData);
        this.exitAddSuggestion();
    };

    exitAddSuggestion = () => {
        this.setState({
            open: false,
            target: undefined,
            parentTarget: undefined,
            text: "",
            user: "All",
            labelIsValid: false,
            suggestedLabel: "",
            lang: this.props.defaultLang,
            labelLang: this.props.defaultLang,
            suggestionType: this.props.defaultType || undefined
        });
    };

    selectLanguage = (lang) => this.setState({ lang });

    selectLabelLanguage = (labelLang) => this.setState({ labelLang, suggestedLabel: "" });

    handleChange = event => {
        const t = event.target;
        const cleanText = lightSanitiseText(t.value);
        this.setState({ [t.name]: cleanText });
    };

    handleTypeChange = (projectLang) => async (suggestionType) => {
        if (suggestionType === "RemovalSuggestion" || suggestionType === "MovingSuggestion") {
            await this.loadConcepts();
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({ suggestionType, target: undefined, text: "" });
        }
        else {
            let labelLang = this.state.labelLang;
            if (suggestionType === "LabelSuggestion" && "prefLabel" in this.props.data) {
                labelLang = projectLang.find(pl => !Object.keys(this.props.data.prefLabel).includes(pl));
            }
            this.setState({ suggestionType, target: undefined, parentTarget: undefined, suggestedLabel: "", text: "", labelLang });
        }
    };

    setTarget = (target) => this.setState({ target });

    removeTarget = () => this.setState({ target: null });

    setParentTarget = (parentTarget) => this.setState({ parentTarget });

    removeParentTarget = () => this.setState({ parentTarget: null });

    labelIsValid = (suggestedLabel, isValid) => this.setState({ suggestedLabel, labelIsValid: isValid });

    handleUserChange = (user) => this.setState({ user });

    render() {

        const { classes, name, sourceConcept, graph, optionForXL, projectIRI, flat,
            schemeIRI, data, isSchemeLevel, defaultType, useWorkflow } = this.props;
        const { text, lang, suggestionType, target, parentTarget, labelIsValid, user, allowNotifications,
            suggestedLabel, inSchemeResources, isErrored, labelLang } = this.state;

        if (isErrored) return <Errored data={isErrored} />;

        const selectedOption = SUGGESTION_TYPE_OPTIONS.find(option => option.value === suggestionType);
        let targetTitle = selectedOption && selectedOption["targetTitle"];
        const targetRelationship = selectedOption && selectedOption["targetRelationship"];
        const labelRequired = selectedOption && selectedOption["labelRequired"];
        if (selectedOption && !selectedOption.targetMandatory) targetTitle += " (Leave Blank for Top Concept)";

        return (
            <ProjectConsumer>
                {({ projectLang, defaultLang }) => (
                    <React.Fragment>
                        <CreateButton dataTest={"add-suggestion"} onClick={this.handleClickOpen} label="Add Suggestion" />
                        <Dialog
                            data-test="add-suggestion-dialog"
                            maxWidth="xl"
                            fullWidth
                            className={classes.root}
                            classes={{
                                paperWidthXl: classes.xLargeDialog
                            }}
                            onClose={this.exitAddSuggestion}
                            open={this.state.open}
                            aria-labelledby="text-dialog-title">
                            <DialogTitle data-test="add-suggestion-title" disableTypography className={classes.mainHeading} id="text-dialog-title">Add Suggestion</DialogTitle>
                            <DialogContent data-test="add-suggestion-dialog-content"
                                style={{
                                    maxHeight: '500px',
                                    overflowY: 'visible'
                                }}>
                                <Help textRef="ADD_SUGGESTION" />
                                <form autoComplete="off" noValidate="novalidate">
                                    <div style={{ display: 'flex' }}>
                                        <div data-test="suggestion-selection" className={classes.bigPanel}>
                                            <Typography className={classes.subMainHeading}>What Are You Suggesting?</Typography>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ flex: 1 }}>
                                                    <TitledPanel dataTest="suggestion-type" height="full" title="Suggestion Type">
                                                        <MenuSelector
                                                            dataTest="type-selector"
                                                            selected={defaultType}
                                                            handleChange={this.handleTypeChange(projectLang)}
                                                            hintWord="suggestion type"
                                                            options={SUGGESTION_TYPE_OPTIONS
                                                                .filter(suggestionType => (suggestionType.value === "LabelSuggestion" && data.prefLabel && !projectLang.every(pl => Object.keys(data.prefLabel).includes(pl)))
                                                                    || suggestionType.value !== "LabelSuggestion")
                                                                .filter(suggestionType => isSchemeLevel ? suggestionType.schemeLevel : true)
                                                                .filter(suggestionType => suggestionType.value === "NewConceptSuggestion" && flat ? false : true)
                                                                .filter(suggestionType => {
                                                                    if ("requiredRelationship" in suggestionType
                                                                        && (!data[suggestionType.requiredRelationship]
                                                                            || data[suggestionType.requiredRelationship].length === 0)) return false;
                                                                    if (suggestionType.active) return true;
                                                                    return false;
                                                                })} />
                                                    </TitledPanel>
                                                </div>
                                            </div>
                                            {labelRequired &&
                                                <div>
                                                    {selectedOption["selectLabelLanguage"] &&
                                                        <TitledPanel dataTest="label-language" title="Label Language" margin={true}>
                                                            <ProjectLanguageSelector
                                                                selectedLang={suggestionType === "LabelSuggestion" && data.prefLabel ?
                                                                    projectLang.find(pl => !Object.keys(data.prefLabel).includes(pl))
                                                                    :
                                                                    lang}
                                                                includeLabel={false}
                                                                excludeLangs={suggestionType === "LabelSuggestion" && data.prefLabel ?
                                                                    Object.keys(data.prefLabel)
                                                                    :
                                                                    []
                                                                }
                                                                onSelect={this.selectLabelLanguage} />
                                                        </TitledPanel>
                                                    }
                                                    {suggestionType === "SynonymSuggestion" ?
                                                        <SuggestionAltLabelField
                                                            id={sourceConcept}
                                                            isValid={this.labelIsValid}
                                                            schemeIRI={schemeIRI}
                                                            optionForXL={optionForXL}
                                                            graph={graph}
                                                            key={labelLang}
                                                            lang={labelLang} />
                                                        :
                                                        suggestionType === "LabelSuggestion" ?
                                                            <SuggestionPrefLabelField
                                                                id={sourceConcept}
                                                                isValid={this.labelIsValid}
                                                                schemeIRI={schemeIRI}
                                                                optionForXL={optionForXL}
                                                                graph={graph}
                                                                key={labelLang}
                                                                lang={labelLang} />
                                                            :
                                                            <SuggestionField
                                                                isValid={this.labelIsValid}
                                                                schemeIRI={schemeIRI}
                                                                optionForXL={optionForXL}
                                                                graph={graph}
                                                                key={labelLang}
                                                                lang={labelLang} />
                                                    }
                                                </div>
                                            }
                                            {selectedOption && selectedOption["parentTitle"] && inSchemeResources.length > 0 &&
                                                <TitledPanel
                                                    dataTest="select-parent-target"
                                                    overflow={true}
                                                    margin={true}
                                                    title={selectedOption["parentTitle"]}>
                                                    {parentTarget ?
                                                        <div>
                                                            <ConceptChip
                                                                id={parentTarget}
                                                                graph={graph}
                                                                onRemove={this.removeParentTarget}
                                                                loadOptions={{
                                                                    language: defaultLang
                                                                }}
                                                                defaultLang={defaultLang} />
                                                        </div>
                                                        :
                                                        <div data-test="broader-selection" style={{ maxHeight: 80, overflowY: 'scroll' }}>
                                                            {inSchemeResources.map(parentTargetConceptOption =>
                                                                <ConceptChip
                                                                    key={parentTargetConceptOption}
                                                                    id={parentTargetConceptOption}

                                                                    graph={graph}
                                                                    loadOptions={{
                                                                        language: defaultLang
                                                                    }}
                                                                    onSelect={() => this.setParentTarget(parentTargetConceptOption)}
                                                                    defaultLang={defaultLang} />
                                                            )}
                                                        </div>
                                                    }
                                                </TitledPanel>
                                            }
                                            {selectedOption && selectedOption["targetTitle"] &&
                                                <TitledPanel
                                                    overflow={true}
                                                    margin={true}
                                                    title={targetTitle}>
                                                    {target ?
                                                        <ConceptChip
                                                            id={target}
                                                            graph={graph}
                                                            onRemove={this.removeTarget}
                                                            loadOptions={{
                                                                language: defaultLang
                                                            }}
                                                            defaultLang={defaultLang} />
                                                        :
                                                        targetRelationship ?
                                                            <div style={{ maxHeight: 80, overflow: 'scroll' }}>
                                                                {data[targetRelationship].map(targetConceptOption =>
                                                                    <ConceptChip
                                                                        key={targetConceptOption}
                                                                        id={targetConceptOption}
                                                                        graph={graph}
                                                                        loadOptions={{
                                                                            language: defaultLang
                                                                        }}
                                                                        onSelect={() => this.setTarget(targetConceptOption)}
                                                                        defaultLang={defaultLang} />
                                                                )}
                                                            </div>
                                                            :
                                                            <div style={{ height: 43, overflow: 'visible' }}>
                                                                <FindConcept
                                                                    source={sourceConcept}
                                                                    schemeIRI={schemeIRI}
                                                                    graph={graph}
                                                                    useWorkflow={useWorkflow}
                                                                    exclude={[sourceConcept]}
                                                                    onSelectConcept={this.setTarget} />
                                                            </div>
                                                    }
                                                </TitledPanel>
                                            }
                                        </div>
                                        <div data-test="detail" className={classes.bigPanel}>
                                            <Typography className={classes.subMainHeading}>Detail</Typography>
                                            <TitledPanel dataTest="detail-language" title="Language">
                                                <ProjectLanguageSelector selectedLang={lang} includeLabel={false} onSelect={this.selectLanguage} />
                                            </TitledPanel>
                                            <TitledTextField
                                                dataTest="language-text"
                                                disabled={!suggestionType}
                                                name="text"
                                                title={name || "Detail"}
                                                margin={true}
                                                lang={lang}
                                                placeholder="Enter detail about your suggestion"
                                                required={true}
                                                text={text}
                                                multiline={true}
                                                rows={5}
                                                maxLength={500}
                                                onEnter={text.length > 0 && lang !== "" ? this.addItem : null}
                                                onChange={this.handleChange} />
                                        </div>
                                        <div data-test="assignment" className={classes.bigPanel}>
                                            <Typography className={classes.subMainHeading}>Assignment</Typography>
                                            {allowNotifications ?
                                                <div>
                                                    <TitledPanel dataTest="assignee" height="full" title="Assign To">
                                                        <SubscriptionUsers
                                                            projectIRI={projectIRI}
                                                            allLabel="Unassigned"
                                                            includeLabel={false}
                                                            onUserChange={this.handleUserChange} />
                                                    </TitledPanel>
                                                </div>
                                                :
                                                <div data-test="no-access" style={{ textAlign: 'center' }}>
                                                    <Typography align="center" variant="body2">Assignment is not available with your current subscription.</Typography>
                                                    <Typography align="center" variant="body2">This feature requires a minimum of a Premium subscription.</Typography>
                                                    <Upgrade />
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </form>
                            </DialogContent>
                            <DialogMultiActions onCancel={this.exitAddSuggestion}>
                                <PrimaryButton
                                    dataTest="add-suggestion-button"
                                    disabled={labelIsValid || lang === "" || text.length === 0 ||
                                        (labelRequired && suggestedLabel === "") ||
                                        (selectedOption.parentTitle && !parentTarget && data["broader"] && data["broader"].length > 0) ||
                                        !suggestionType || (selectedOption && selectedOption.targetMandatory && !target)}
                                    onClick={() => this.addSuggestion(false)}>Add</PrimaryButton>
                                {user !== "All" &&
                                    <PrimaryButton
                                        dataTest="add-notify-suggestion-button"
                                        disabled={labelIsValid || lang === "" || text.length === 0 ||
                                            (labelRequired && suggestedLabel === "") ||
                                            (selectedOption.parentTitle && !parentTarget && data["broader"] && data["broader"].length > 0) ||
                                            !suggestionType || (selectedOption && selectedOption.targetMandatory && !target)}
                                        onClick={() => this.addSuggestion(true)}>Add and Notify</PrimaryButton>
                                }
                            </DialogMultiActions>
                        </Dialog>
                    </React.Fragment>
                )}
            </ProjectConsumer>
        );
    }
}

AddSuggestionDialog.defaultProps = {
    useWorkflow: false,
    flat: false
};

AddSuggestionDialog.propTypes = {
    defaultType: PropTypes.string,
    isSchemeLevel: PropTypes.bool.isRequired,
    data: PropTypes.object.isRequired,
    schemeIRI: PropTypes.string.isRequired,
    useWorkflow: PropTypes.bool,
    flat: PropTypes.bool,
    projectIRI: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    sourceConcept: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    optionForXL: PropTypes.bool.isRequired,
    onAddSuggestion: PropTypes.func.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(AddSuggestionDialog);